import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output, inject, DestroyRef } from '@angular/core';
import { NotifEngineService } from './notif-engine.service';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
/**
 * Angular 12 migration of admin/notifEngine.
 * Replicates notifListCtrl: notif type dropdown, date filters (type 1), Go, grid from loadGridJson.
 */
@Component({
    selector: 'gtw-notif-engine',
    templateUrl: './notif-engine.component.html',
    styleUrls: ['./notif-engine.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NotifEngineComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  filterParams: any = {};
  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  private isInitialized = false;
  private isLoading = false;

  title = 'Notifications Engine';
  notifs = [
    { label: 'View All Notifications', value: 1 },
    { label: 'View Master Data', value: 2 },
    { label: 'Maintenance Notifications', value: 3 },
  ];
  notifType = 1;
  dateFilterFrom: string = '';
  dateFilterTo: string = '';
  gridPayload: any = null;
  loading = false;
  errorMessage: string | null = null;
  showGrid = false;
  selectedNotif: any = null;
  activeDetailTab: 'details' | 'publishers' | 'render' | 'writer' = 'details';
  showMaintenancePanel = false;
  maintenanceRow: any = null;

  @Input('base-urls')
  set _base_urls(v: string) {
    try {
      const urls = safeJsonInput<any>(v || '{}', null, 'notif-engine.parse1');
      if (Object.keys(urls).length > 0) {
        this.baseURLs = urls;
        this.tryInit();
      }
    } catch (_) {}
  }

  @Input('header-data')
  set _header_data(v: string) {
    try {
      const header = safeJsonInput<any>(v || '{}', null, 'notif-engine.parse2');
      if (Object.keys(header).length > 0) {
        this.globalService = header.globalParams;
        this.GENERAL_CONFIG = header.generalConfigData;
        this.tryInit();
      }
    } catch (_) {}
  }

  @Input('row-data')
  set _row_data(v: string) {
    try {
      this.rowData = safeJsonInput<any>(v || '{}', null, 'notif-engine.parse3');
      this.tryInit();
    } catch (_) {}
  }

  @Input('filter-params')
  set _filter_params(v: string) {
    try {
      const params = safeJsonInput<any>(v || '{}', null, 'notif-engine.parse4');
      this.filterParams = params;
      this.tryInit();
    } catch (_) {}
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private notifEngineService: NotifEngineService
  ) {}

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    if (!this.filterParams || Object.keys(this.filterParams).length === 0) return;
    this.isLoading = true;
    this.isInitialized = true;
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  go(): void {
    this.loadData();
  }

  loadData(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url;
    if (!baseUrl) {
      this.errorMessage = 'Base URL not configured.';
      this.cdr.detectChanges();
      return;
    }
    this.loading = true;
    this.showGrid = true;
    this.selectedNotif = null;
    this.errorMessage = null;
    this.cdr.detectChanges();

    const args = this.notifType === 1
      ? { start_dt: this.dateFilterFrom || '', end_dt: this.dateFilterTo || '' }
      : undefined;

    this.notifEngineService
      .loadGridData(baseUrl, this.globalService, this.filterParams, this.notifType, args).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data && data.callSuccess === '1') {
            this.gridPayload = data;
            this.errorMessage = null;
          } else {
            this.gridPayload = null;
            this.errorMessage = (data && data.errorMessage) || 'Failed to load data.';
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.gridPayload = null;
          this.errorMessage = err?.error?.errorMessage || err?.message || 'Error loading data.';
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  onNotifChange(): void {
    this.showGrid = false;
    this.selectedNotif = null;
    this.showMaintenancePanel = false;
    this.maintenanceRow = null;
    this.cdr.detectChanges();
  }

  openNotifDetails(row: any): void {
    this.selectedNotif = row;
    this.activeDetailTab = 'details';
    this.showMaintenancePanel = false;
    this.cdr.detectChanges();
  }

  setDetailTab(tab: 'details' | 'publishers' | 'render' | 'writer'): void {
    this.activeDetailTab = tab;
    this.cdr.detectChanges();
  }

  closeNotifDetails(): void {
    this.selectedNotif = null;
    this.cdr.detectChanges();
  }

  saveNotifDetails(): void {
    // Legacy modal "save" closes the dialog; no dedicated save API call existed here.
    this.closeNotifDetails();
  }

  openMaintenance(row?: any): void {
    this.maintenanceRow = row || null;
    this.showMaintenancePanel = true;
    this.selectedNotif = null;
    this.cdr.detectChanges();
  }

  closeMaintenance(): void {
    this.showMaintenancePanel = false;
    this.maintenanceRow = null;
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.loadData();
    this.requestGridUpdate.emit('refresh');
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  get gridRows(): any[] {
    if (!this.gridPayload?.data) return [];
    const d = this.gridPayload.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.DATA)) return d.DATA;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  }

  get gridColumns(): string[] {
    const rows = this.gridRows;
    if (!rows.length) return [];
    const first = rows[0];
    return typeof first === 'object' && first !== null ? Object.keys(first) : [];
  }

  get hasNotifDetails(): boolean {
    return !!this.selectedNotif;
  }
}
